/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Separator from 'flarum/common/components/Separator';
import icon from 'flarum/common/helpers/icon';
import type Mithril from 'mithril';
import type LiteraryWork from 'flarum/orell-articles/models/LiteraryWork';
import RatingUsersListModal, { type RatingFilter } from './RatingUsersListModal';

/** One row of the histogram: how many ratings a given level received, and its share of the total. */
export interface AggregatedRating {
	rating: number;
	count: number;
	/** `count` divided by the total number of ratings, i.e. already a fraction of 1. */
	percent: number;
}

export interface RatingSummaryAttrs extends ComponentAttrs {
	/** One entry per rating level, ascending. Rendered top-down, so reversed here. */
	aggregatedRatings: AggregatedRating[];
	/** The number of reviews that have a text body, as opposed to a bare rating. */
	totalReviewCount: number;
	work: LiteraryWork;
}

export default class RatingSummary extends Component<RatingSummaryAttrs> {
	oninit(vnode: Mithril.Vnode<RatingSummaryAttrs, this>) {
		super.oninit(vnode);
	}

	view() {
		const { aggregatedRatings, totalReviewCount, work } = this.attrs;

		return (
			<div className="RatingSummary">
				{aggregatedRatings
					.slice()
					.reverse()
					.map(({ rating, count, percent }) => (
						<div className="RatingSummary-item">
							<label className="RatingSummary-label">
								<span>{rating}</span> {icon('far fa-heart')}
							</label>
							<RatingBar value={percent} />
							<RatingValue rating={rating} count={count} work={work.id()} />
						</div>
					))}
				<Separator />
				<div className="RatingSummary-item RatingSummary-written">
					<label className="RatingSummary-label">{icon('far fa-comment-alt-lines')}</label>
					<RatingValue rating="isTextReview" count={totalReviewCount} work={work.id()} />
				</div>
			</div>
		);
	}
}

interface RatingBarAttrs extends ComponentAttrs {
	/** Fill level as a fraction of 1. */
	value: number;
}

class RatingBar extends Component<RatingBarAttrs> {
	oninit(vnode: Mithril.Vnode<RatingBarAttrs, this>) {
		super.oninit(vnode);
	}

	view() {
		const { value } = this.attrs;
		return (
			<span className="RatingBar">
				<span className="RatingBar-fill" style={{ width: `${value * 100}%` }}></span>
			</span>
		);
	}
}

interface RatingValueAttrs extends ComponentAttrs {
	rating: RatingFilter;
	count: number;
	/** The literary work's id, forwarded to the raters modal as a filter. */
	work: string | undefined;
}

class RatingValue extends Component<RatingValueAttrs> {
	oninit(vnode: Mithril.Vnode<RatingValueAttrs, this>) {
		super.oninit(vnode);
	}

	view() {
		const { rating, count, work } = this.attrs;
		const hasItems = count > 0;

		return (
			<span
				className={`RatingSummary-value ${hasItems ? 'hasItems' : ''}`}
				onclick={
					hasItems
						? () =>
								app.modal.show(RatingUsersListModal, {
									work,
									rating,
								})
						: undefined
				}
			>
				{count}
			</span>
		);
	}
}
